import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Check, Crown, Star } from 'lucide-react';
import { useState } from 'react';
import { cn } from '@/lib/utils';

interface PlanFeature {
  title: string;
  included: boolean;
}

interface SubscriptionPlan {
  name: string;
  price: number;
  period: string;
  features: PlanFeature[];
  popular?: boolean;
  buttonText: string;
  description: string;
}

const plans: SubscriptionPlan[] = [
  {
    name: 'Basic',
    price: 29,
    period: 'month',
    description: 'Perfect for small salons getting started',
    buttonText: 'Start Basic',
    features: [
      { title: 'Up to 3 staff members', included: true },
      { title: 'Basic appointment scheduling', included: true },
      { title: 'Customer management', included: true },
      { title: 'Email notifications', included: true },
      { title: 'Basic analytics', included: true },
      { title: 'Advanced reporting', included: false },
      { title: 'Multiple locations', included: false },
      { title: 'Custom branding', included: false },
    ],
  },
  {
    name: 'Professional',
    price: 79,
    period: 'month',
    description: 'Ideal for growing businesses',
    buttonText: 'Upgrade to Pro',
    popular: true,
    features: [
      { title: 'Up to 10 staff members', included: true },
      { title: 'Advanced appointment scheduling', included: true },
      { title: 'Customer management', included: true },
      { title: 'Email & SMS notifications', included: true },
      { title: 'Advanced analytics', included: true },
      { title: 'Advanced reporting', included: true },
      { title: 'Multiple locations', included: true },
      { title: 'Custom branding', included: false },
    ],
  },
  {
    name: 'Enterprise',
    price: 199,
    period: 'month',
    description: 'For large scale operations',
    buttonText: 'Contact Sales',
    features: [
      { title: 'Unlimited staff members', included: true },
      { title: 'Advanced appointment scheduling', included: true },
      { title: 'Customer management', included: true },
      { title: 'Email & SMS notifications', included: true },
      { title: 'Advanced analytics', included: true },
      { title: 'Advanced reporting', included: true },
      { title: 'Multiple locations', included: true },
      { title: 'Custom branding', included: true },
    ],
  },
];

const SubscriptionPlanTab = () => {
  const [currentPlan] = useState('Basic'); // Replace with actual current plan from your state management

  return (
    <div className="space-y-6">
      {/* Current Plan Status */}
      <Card className="w-full bg-gradient-to-r from-blue-50 to-indigo-50">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold mb-2">Your Current Plan</h2>
              <p className="text-gray-600">
                You are currently on the <span className="font-semibold">{currentPlan}</span> plan
              </p>
            </div>
            <Crown className="h-12 w-12 text-blue-600" />
          </div>
        </CardContent>
      </Card>

      {/* Subscription Plans */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {plans.map((plan) => (
          <Card
            key={plan.name}
            className={cn(
              'relative overflow-hidden',
              plan.popular && 'border-2 border-blue-500 shadow-lg'
            )}
          >
            {plan.popular && (
              <div className="absolute top-0 right-0">
                <div className="bg-blue-500 text-white px-3 py-1 rounded-bl-lg text-sm font-medium flex items-center gap-1">
                  <Star className="h-4 w-4" /> Popular
                </div>
              </div>
            )}
            <CardContent className="p-6">
              <h3 className="text-xl font-bold mb-2">{plan.name}</h3>
              <p className="text-gray-600 mb-4">{plan.description}</p>
              <div className="mb-6">
                <span className="text-4xl font-bold">${plan.price}</span>
                <span className="text-gray-600">/{plan.period}</span>
              </div>
              <Button
                className={cn(
                  'w-full mb-6',
                  plan.popular ? 'bg-blue-500 hover:bg-blue-600' : 'bg-gray-900 hover:bg-gray-800'
                )}
              >
                {plan.buttonText}
              </Button>
              <div className="space-y-3">
                {plan.features.map((feature, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <Check
                      className={cn(
                        'h-5 w-5',
                        feature.included ? 'text-green-500' : 'text-gray-300'
                      )}
                    />
                    <span
                      className={cn(
                        'text-sm',
                        feature.included ? 'text-gray-900' : 'text-gray-400'
                      )}
                    >
                      {feature.title}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Additional Information */}
      <Card className="w-full">
        <CardContent className="p-6">
          <h3 className="text-lg font-semibold mb-4">Need Help Choosing?</h3>
          <p className="text-gray-600">
            Contact our sales team for a personalized demo and help selecting the right plan for your business.
          </p>
          <Button variant="outline" className="mt-4">
            Contact Sales
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default SubscriptionPlanTab;
